export const MIN_PASSWORD_LENGTH = 8;

export interface PasswordRequirements {
  minLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
}

/**
 * Validates a password against some security requirements
 * @param password - The password string to validate
 * @returns PasswordRequirements object with boolean flags for each requirement
 */
export const validatePassword = (password: string): PasswordRequirements => {
  return {
    minLength: password.length >= MIN_PASSWORD_LENGTH,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
  };
};

/**
 * Checks if a password meets all security requirements
 * @param requirements - PasswordRequirements object from validatePassword
 * @returns boolean indicating if all requirements are met
 */
export const isPasswordValid = (
  requirements: PasswordRequirements
): boolean => {
  return Object.values(requirements).every(Boolean);
};

/**
 * Gets a list of password requirement descriptions for UI display
 * @returns Array of requirement descriptions
 */
export const getPasswordRequirementDescriptions = () => [
  {
    key: 'minLength' as keyof PasswordRequirements,
    text: `At least ${MIN_PASSWORD_LENGTH} characters`,
  },
  {
    key: 'hasUppercase' as keyof PasswordRequirements,
    text: 'One uppercase letter (A-Z)',
  },
  {
    key: 'hasLowercase' as keyof PasswordRequirements,
    text: 'One lowercase letter (a-z)',
  },
  { key: 'hasNumber' as keyof PasswordRequirements, text: 'One number (0-9)' },
];
